import { toSentenceCase } from '@/lib/utilities'
import { storeRoutes } from '@/lib/routes/store'
import { MenuSection } from '@/types/interfaces/menu'
import { BOOK_VISIT_DETAIL_HREF } from '@/lib/routes/booking'
import { PRESCRIPTION_RENEWAL_HREF } from './sku-constants'

export interface PageCategory {
    /** URL‑friendly slug, e.g. `cold-flu` */
    slug: string
    /** Display name shown to users, e.g. `Cold & Flu` */
    name: string
    /** url to navigate to */
    href: string

    /** Optional for overriding menu items */
    overrideSection?: MenuSection
}

export interface HoverCategoryColumn {
    name: string
    items: PageCategory[]
}

export interface HoverCategory {
    buttonTitle: string
    columns: HoverCategoryColumn[]
}

export const BrowseHoverCategory: HoverCategory = {
    buttonTitle: 'Browse all',
    columns: [
        {
            name: 'Quick care',
            items: [
                { slug: 'video-visit', name: 'Video visit', href: BOOK_VISIT_DETAIL_HREF },
                { slug: 'urgent-care', name: 'Urgent care', href: '/s/urgent-care/400002' },
                { slug: 'primary-care', name: 'Primary care visit', href: '/s/primary-care-visit/100012' },
                { slug: 'doctors-note', name: "Doctor's note", href: '/s/doctor-s-note/400001' },
                { slug: 'presctiption-renewal', name: 'Prescription renewal', href: PRESCRIPTION_RENEWAL_HREF },
                { slug: 'uti-evaluation', name: 'UTI evaluation', href: '/s/uti-evaluation/300001n' },
                { slug: 'migraine', name: 'Migraine', href: '/s/migraine/300007' },
            ],
        },
        {
            name: 'Health categories',
            items: [
                { slug: 'weight-loss', name: 'Weight loss', href: '/store/weight-loss' },
                { slug: 'cold-flu', name: 'Cold & Flu', href: '/store/cold-flu' },
                { slug: 'digestive-care', name: 'Digestive', href: '/store/digestive-care' },
                { slug: 'skin', name: 'Skin', href: '/store/skin' },
                { slug: 'allergies', name: 'Allergies', href: '/store/allergies' },
                { slug: 'pain-relief', name: 'Pain relief', href: '/store/pain-relief' },
                { slug: 'mental-health', name: 'Mental health', href: '/store/mental-health' },
                { slug: 'heart-health', name: 'Heart health', href: '/store/heart-health' },
                { slug: 'diabetes-care', name: 'Diabetes care', href: '/store/diabetes-care' },
                { slug: 'womens-health', name: "Women's health", href: '/store/womens-health' },
                { slug: 'mens-health', name: "Men's health", href: '/store/mens-health' },
                { slug: 'respiratory', name: 'Respiratory', href: '/store/respiratory' },
                { slug: 'sleep', name: 'Sleep', href: '/store/sleep' },
            ],
        },
        {
            name: 'Specialties',
            items: [
                { slug: 'dermatology', name: 'Dermatology', href: '/store/dermatology' },
                { slug: 'cardiology', name: 'Cardiology', href: '/store/cardiology' },
                { slug: 'endocrinology', name: 'Endocrinology', href: '/store/endocrinology' },
                { slug: 'gastroenterology', name: 'Gastroenterology', href: '/store/gastroenterology' },
                { slug: 'musculoskeletal', name: 'Musculoskeletal', href: '/store/musculoskeletal' },
                { slug: 'neurology', name: 'Neurology', href: '/store/neurology' },
                { slug: 'rheumatology', name: 'Rheumatology', href: '/store/rheumatology' },
                { slug: 'urology', name: 'Urology', href: '/store/urology' },
            ],
        },
        {
            name: 'Labs & Imaging',
            items: [
                { slug: 'annual-wellness', name: 'Annual wellness', href: '/s/annual-wellness-test/500001' },
                { slug: 'thyroid-panel', name: 'Thyroid panel', href: '/s/thyroid-panel-with-tsh/500093' },
                { slug: 'vitamin-d-test', name: 'Vitamin D test', href: '/s/vitamin-d-test/500010' },
                { slug: 'a1c-check-up', name: 'A1c check-up', href: '/s/a1c-diabetes-and-prediabetes-check-up/500041' },
                { slug: 'std-panel', name: 'STD panel', href: '/s/comprehensive-std-panel/500014' },
                { slug: 'celiac-panel', name: 'Celiac panel', href: '/s/celiac-disease-panel/500063' },
                { slug: 'fatigue-panel', name: 'Fatigue panel', href: '/s/comprehensive-fatigue-panel/500004' },
                { slug: 'cbc-test', name: 'CBC test', href: '/s/complete-blood-count-cbc-test/500007' },
                { slug: 'lipid-panel', name: 'Lipid panel', href: '/s/lipid-panel-plus/500012' },
                { slug: 'labs', name: 'Browse more', href: '/store/labs' },
            ],
        },
        {
            name: 'Medications',
            items: [
                { slug: 'rx-renewal', name: 'Rx renewal', href: PRESCRIPTION_RENEWAL_HREF },
                { slug: 'new-medication', name: 'New medication', href: '/s/new-medication-consult/200011' },
                { slug: 'glp-1-consult', name: 'GLP-1 consult', href: '/s/glp1-medication-evaluation/300543' },
                { slug: 'tamiflu', name: 'Tamiflu', href: '/s/tamiflu/200008' },
                { slug: 'lipitor', name: 'Lipitor (Atorvastatin)', href: '/s/lipitor-atorvastatin/202613' },
                { slug: 'ondansetron', name: 'Ondansetron (Zofran)', href: '/s/ondansetron-zofran/200123' },
                { slug: 'metformin', name: 'Metformin', href: '/s/metformin/200003' },
                { slug: 'nurtec', name: 'Nurtec (Rimegepant)', href: '/s/nurtec-rimegepant/200790' },
                { slug: 'naltrexone', name: 'Naltrexone', href: '/s/naltrexone/202637' },
                { slug: 'chantix', name: 'Chantix (varenicline)', href: '/s/chantix-varenicline/200411' },
                { slug: 'sildenafil', name: 'Sildenafil (Viagra)', href: '/s/sildenafil-viagra/200217' },
                { slug: 'epipen', name: 'EpiPen', href: '/s/epipen-epinephrine-auto-injector/200331' },
                { slug: 'medications', name: 'Browse more', href: '/store/medications' },
            ],
        },
    ],
}

export const EverydayCategories: PageCategory[] = [
    { slug: 'weight-loss', name: 'Weight loss', href: '/store/weight-loss' },
    { slug: 'cold-flu', name: 'Cold & flu', href: '/store/cold-flu' },
    { slug: 'digestive-care', name: 'Digestive', href: '/store/digestive-care' },
    { slug: 'skin', name: 'Skin', href: '/store/skin' },
    { slug: 'allergies', name: 'Allergies', href: '/store/allergies' },
    { slug: 'pain-relief', name: 'Pain relief', href: '/store/pain-relief' },
    { slug: 'mental-health', name: 'Mental health', href: '/store/mental-health' },
    { slug: 'heart-health', name: 'Heart health', href: '/store/heart-health' },
    { slug: 'diabetes-care', name: 'Diabetes care', href: '/store/diabetes-care' },
    { slug: 'womens-health', name: "Women's health", href: '/store/womens-health' },
    { slug: 'mens-health', name: "Men's health", href: '/store/mens-health' },
    { slug: 'respiratory', name: 'Respiratory', href: '/store/respiratory' },
    { slug: 'sleep', name: 'Sleep', href: '/store/sleep' },
]

export const SpecialistCategories: PageCategory[] = [
    { slug: 'cardiology', name: 'Cardiology', href: '/store/cardiology' },
    { slug: 'dermatology', name: 'Dermatology', href: '/store/dermatology' },
    { slug: 'endocrinology', name: 'Endocrinology', href: '/store/endocrinology' },
    { slug: 'gastroenterology', name: 'Gastroenterology', href: '/store/gastroenterology' },
    { slug: 'musculoskeletal', name: 'Musculoskeletal', href: '/store/musculoskeletal' },
    { slug: 'neurology', name: 'Neurology', href: '/store/neurology' },
    { slug: 'rheumatology', name: 'Rheumatology', href: '/store/rheumatology' },
    { slug: 'urology', name: 'Urology', href: '/store/urology' },
]

export const GetCareCategories: PageCategory[] = [
    { slug: 'home', name: 'Store', href: '/store/home' },
    { slug: 'everyday-care', name: 'Everyday care', href: '/store/everyday-care' },
    {
        slug: 'specialty-care',
        name: 'Specialty care +',
        href: '/store/specialty-care',
        overrideSection: {
            title: 'Specialty care',
            menuItems: [
                ...SpecialistCategories,
                { slug: 'specialty-care', name: 'See all', href: '/store/specialty-care' },
            ],
        },
    },
    { slug: 'medications', name: 'Medications', href: '/store/medications' },
    { slug: 'labs', name: 'Lab tests', href: '/store/labs' },
    {
        slug: 'browse-all',
        name: 'Browse all',
        href: '#',
        overrideSection: {
            title: 'Browse all',
            menuItems: BrowseHoverCategory.columns.map((column) => ({
                categoryTitle: column.name,
                items: column.items,
            })),
        },
    },
]

export const Categories: readonly PageCategory[] = [
    ...GetCareCategories,
    // Valid store URL for bookmarks; not linked from nav (use medications).
    { slug: 'prescriptions', name: 'Prescriptions', href: '/store/prescriptions' },
    // Shop header destinations. Not linked from the legacy category menu.
    { slug: 'visits', name: 'Visits', href: storeRoutes.visits() },
    { slug: 'specialists', name: 'Specialists', href: storeRoutes.specialists() },
    ...EverydayCategories,
    ...SpecialistCategories,
] as const

/** Union of all valid slugs */
export type PageCategoryType = (typeof Categories)[number]['slug']

export type StoreCategoryPath = `/store/${PageCategoryType}`

/**
 * Returns `true` only when the path is exactly `/store/{STORETAG}` but does not end with `/all-services`.
 * (or has nothing but a trailing slash after it, e.g. `/store/skin/`).
 */
export function isStoreCategoryPath(pathname: string): pathname is StoreCategoryPath {
    if (!pathname.startsWith('/store/')) return false

    // Return false if the path ends with "/all-services"
    if (pathname.endsWith('/all-services')) return false

    // Pull the first segment after `/store/`
    const tag = pathname.slice('/store/'.length).split('/')[0]

    // Runtime check: is that segment one of the category values?
    return Categories.some((c) => c.slug === tag)
}

/**
 * Returns `true` only when the path is exactly `/store/{STORETAG}/all-services`.
 */
export function isStoreCategoryAllPath(pathname: string): boolean {
    if (!pathname.startsWith('/store/')) return false

    if (!pathname.endsWith('/all-services')) return false

    const tag = pathname.slice('/store/'.length).split('/')[0]

    return Categories.some((c) => c.slug === tag)
}

export function getDisplayableName(name: string) {
    const match = Categories.find((c) => c.slug === name)
    return match ? match.name : toSentenceCase(name.replace(/-/g, ' '))
}

export interface StoreTagMetadata {
    title: string
    mobileTitle?: string
    subtitle: string
    slug: string
}

import { SearchHeader as SearchHeaderType } from '@/types/interfaces/store'

export const SearchHeader: SearchHeaderType = {
    id: 'search-header',
    type: 'searchHeader',
    title: 'Instantly order your next',
    linkTitles: [
        {
            label: 'annual physical',
            href: '/search',
        },
        {
            label: 'lab appointment',
            href: '/search',
        },
        {
            label: 'prescription refill',
            href: '/search',
        },
        {
            label: 'video visit',
            href: '/search',
        },
    ],
}
